import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Modal, ConfirmModal } from '@/components/ui/Modal';
import {
  getInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  adjustInventoryStock,
  type InventoryItem,
} from '@/api/inventory';
import { ApiError } from '@/api/client';
import { Search, Package, AlertTriangle, Plus, Pencil, Trash2, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';

const emptyForm = {
  name: '',
  sku: '',
  category: '',
  unit: '个',
  stockQty: '0',
  warningQty: '5',
  unitPrice: '0',
  remark: '',
};

type FormState = typeof emptyForm;

function ItemForm({
  value,
  onChange,
  isEdit,
}: {
  value: FormState;
  onChange: (v: FormState) => void;
  isEdit: boolean;
}) {
  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    onChange({ ...value, [k]: e.target.value });

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-sm font-medium text-[#334155]">配件名称 *</label>
        <Input value={value.name} onChange={set('name')} placeholder="如：机油滤芯" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-[#334155]">SKU</label>
          <Input value={value.sku} onChange={set('sku')} placeholder="可选" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-[#334155]">分类</label>
          <Input value={value.category} onChange={set('category')} placeholder="如：滤清器" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-[#334155]">单位</label>
          <Input value={value.unit} onChange={set('unit')} placeholder="个/瓶/套" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-[#334155]">单价（元）</label>
          <Input type="number" min="0" step="0.01" value={value.unitPrice} onChange={set('unitPrice')} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {!isEdit && (
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">初始库存</label>
            <Input type="number" min="0" value={value.stockQty} onChange={set('stockQty')} />
          </div>
        )}
        <div>
          <label className="mb-1 block text-sm font-medium text-[#334155]">预警线</label>
          <Input type="number" min="0" value={value.warningQty} onChange={set('warningQty')} />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-[#334155]">备注</label>
        <Input value={value.remark} onChange={set('remark')} placeholder="可选" />
      </div>
    </div>
  );
}

export function InventoryPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleting, setDeleting] = useState<InventoryItem | null>(null);
  const [adjusting, setAdjusting] = useState<{ item: InventoryItem; type: 'inbound' | 'outbound' } | null>(null);
  const [adjustQty, setAdjustQty] = useState('1');
  const [adjustRemark, setAdjustRemark] = useState('');
  const [error, setError] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['inventory', search],
    queryFn: () => getInventoryItems(search || undefined),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['inventory'] });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const base = {
        name: form.name.trim(),
        sku: form.sku.trim() || undefined,
        category: form.category.trim() || undefined,
        unit: form.unit.trim() || '个',
        warningQty: parseInt(form.warningQty, 10) || 0,
        unitPrice: form.unitPrice || '0',
        remark: form.remark.trim() || undefined,
      };
      if (editing) {
        return updateInventoryItem(editing.id, base);
      }
      return createInventoryItem({
        ...base,
        stockQty: parseInt(form.stockQty, 10) || 0,
      });
    },
    onSuccess: () => {
      setFormOpen(false);
      setEditing(null);
      setForm(emptyForm);
      setError('');
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '保存失败'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteInventoryItem(id),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '删除失败'),
  });

  const adjustMutation = useMutation({
    mutationFn: () =>
      adjustInventoryStock(adjusting!.item.id, {
        type: adjusting!.type,
        qty: parseInt(adjustQty, 10) || 0,
        remark: adjustRemark.trim() || undefined,
      }),
    onSuccess: () => {
      setAdjusting(null);
      setAdjustQty('1');
      setAdjustRemark('');
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '操作失败'),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setFormOpen(true);
  };

  const openEdit = (item: InventoryItem) => {
    setEditing(item);
    setForm({
      name: item.name,
      sku: item.sku ?? '',
      category: item.category ?? '',
      unit: item.unit,
      stockQty: String(item.stockQty),
      warningQty: String(item.warningQty),
      unitPrice: item.unitPrice,
      remark: item.remark ?? '',
    });
    setError('');
    setFormOpen(true);
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1E293B]">库存配件</h1>
          <p className="mt-1 text-sm text-[#64748B]">
            管理门店配件库存，实时掌握库存余量与预警状态
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-1 h-4 w-4" />
          新增配件
        </Button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]">{error}</div>
      )}

      {/* 搜索与统计 */}
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索配件名称..."
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-[#64748B]">
              共 <span className="font-medium text-[#1E293B]">{data?.total ?? 0}</span> 个配件
            </span>
            {data && data.warningCount > 0 && (
              <span className="flex items-center gap-1 text-[#D97706]">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">{data.warningCount}</span> 个库存预警
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 库存列表 */}
      <Card>
        <CardHeader>
          <CardTitle>配件列表</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-sm text-[#94A3B8]">加载中...</div>
          ) : isError ? (
            <div className="py-12 text-center text-sm text-[#DC2626]">加载失败，请刷新重试</div>
          ) : !data?.items.length ? (
            <div className="py-12 text-center">
              <Package className="mx-auto h-12 w-12 text-[#E2E8F0]" />
              <p className="mt-3 text-sm text-[#94A3B8]">
                {search ? '未找到匹配的配件' : '暂无库存配件，点击右上角"新增配件"添加'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left text-sm text-[#64748B]">
                    <th className="pb-3 font-medium">配件名称</th>
                    <th className="pb-3 font-medium">分类</th>
                    <th className="pb-3 font-medium">库存数量</th>
                    <th className="pb-3 font-medium">预警线</th>
                    <th className="pb-3 font-medium">单价</th>
                    <th className="pb-3 font-medium">状态</th>
                    <th className="pb-3 font-medium text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {data.items.map((item) => (
                    <tr key={item.id} className="text-sm">
                      <td className="py-3">
                        <div className="font-medium text-[#1E293B]">{item.name}</div>
                        {item.sku && (
                          <div className="mt-0.5 text-xs text-[#94A3B8]">SKU: {item.sku}</div>
                        )}
                      </td>
                      <td className="py-3 text-[#64748B]">{item.category || '-'}</td>
                      <td className="py-3">
                        <span className={`font-medium ${item.warning ? 'text-[#D97706]' : 'text-[#1E293B]'}`}>
                          {item.stockQty} {item.unit}
                        </span>
                      </td>
                      <td className="py-3 text-[#64748B]">{item.warningQty}</td>
                      <td className="py-3 text-[#64748B]">¥{item.unitPrice}</td>
                      <td className="py-3">
                        {item.warning ? (
                          <Badge variant="warning">库存不足</Badge>
                        ) : (
                          <Badge variant="success">正常</Badge>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            title="入库"
                            onClick={() => setAdjusting({ item, type: 'inbound' })}
                            className="rounded-lg p-1.5 text-[#16A34A] hover:bg-[#F0FDF4]"
                          >
                            <ArrowDownToLine className="h-4 w-4" />
                          </button>
                          <button
                            title="出库"
                            onClick={() => setAdjusting({ item, type: 'outbound' })}
                            className="rounded-lg p-1.5 text-[#D97706] hover:bg-[#FFFBEB]"
                          >
                            <ArrowUpFromLine className="h-4 w-4" />
                          </button>
                          <button
                            title="编辑"
                            onClick={() => openEdit(item)}
                            className="rounded-lg p-1.5 text-[#2563EB] hover:bg-[#EFF6FF]"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            title="删除"
                            onClick={() => setDeleting(item)}
                            className="rounded-lg p-1.5 text-[#DC2626] hover:bg-[#FEF2F2]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? '编辑配件' : '新增配件'}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={saveMutation.isPending}>
              取消
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !form.name.trim()}
            >
              {saveMutation.isPending ? '保存中...' : '保存'}
            </Button>
          </>
        }
      >
        <ItemForm value={form} onChange={setForm} isEdit={!!editing} />
      </Modal>

      {/* 删除确认 */}
      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        title="删除配件"
        description={`确定删除配件「${deleting?.name}」吗？删除后不可恢复。`}
        confirmText="删除"
        danger
        loading={deleteMutation.isPending}
      />

      {/* 出入库弹窗 */}
      <Modal
        open={!!adjusting}
        onClose={() => setAdjusting(null)}
        title={adjusting?.type === 'inbound' ? `入库 — ${adjusting.item.name}` : `出库 — ${adjusting?.item.name ?? ''}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setAdjusting(null)} disabled={adjustMutation.isPending}>
              取消
            </Button>
            <Button
              onClick={() => adjustMutation.mutate()}
              disabled={adjustMutation.isPending || !adjustQty || parseInt(adjustQty, 10) <= 0}
            >
              {adjustMutation.isPending ? '提交中...' : '确认'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-lg bg-[#F8FAFC] p-3 text-sm text-[#64748B]">
            当前库存：<span className="font-medium text-[#1E293B]">{adjusting?.item.stockQty} {adjusting?.item.unit}</span>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">数量 *</label>
            <Input
              type="number"
              min="1"
              value={adjustQty}
              onChange={(e) => setAdjustQty(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-[#334155]">备注</label>
            <Input
              value={adjustRemark}
              onChange={(e) => setAdjustRemark(e.target.value)}
              placeholder="如：采购入库 / 工单领料"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
